import './zoom.scss';

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from 'react';
import ZoomMtgEmbedded, { SuspensionViewValue } from '@zoom/meetingsdk/embedded';
import { ZoomComponentConfig, ZoomRole, ZoomViewType } from '../../_models/models';
import { getSoundFileUrl } from '../../_basics/audioPlayer';

type SignatureResponse = { signature: string };

type Props = {
    zoom?: ZoomComponentConfig;
    show: boolean;
    observeParent?: boolean; // default true
    className?: string;
    onConnect?: (actions: { connectAudio: () => void }) => void;
};

const Window: any = window;

export default function ZoomComponentView({ zoom, show, observeParent = true, className, onConnect }: Props) {
    const w = 250;
    const h = 287;

    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const [shareActive, setShareActive] = useState(false);
    //  const [needsAudioConnect, setNeedsAudioConnect] = useState(false);
    // Create an embedded client exactly once (synchronously). This survives parent re-renders.
    //const clientRef = useRef<any>(null);
    if (!Window.zoomApi) {
        Window.zoomApi = ZoomMtgEmbedded.createClient();
        //const  zoom_compatibility = Window.zoomApi.checkSystemRequirements();
    }

    // Use state so React re-renders when the root element appears.
    const [zoomRootEl, setZoomRootEl] = useState<HTMLDivElement | null>(null);
    const zoomRootRef = useCallback((el: HTMLDivElement | null) => {
        setZoomRootEl(el);
    }, []);

    const [joined, setJoined] = useState(false);
    const [joining, setJoining] = useState(false);
    const [size, setSize] = useState<{ width: number; height: number }>({ width: 640, height: 322 });

    const viewType: SuspensionViewValue = 'minimized' || 'active';

    // Track which meeting we actually joined to avoid re-joining on harmless re-renders / new zoom object refs.
    const joinedMeetingRef = useRef<string | null>(null);
    const joinInFlightRef = useRef(false);
    const initDoneRef = useRef(false);
    const joiningMeetingRef = useRef<string | null>(null);

    // // Observe container size (wrapper should fill the TableSlot)
    // useLayoutEffect(() => {
    //     if (!observeParent) return;
    //
    //     // OBSERVE THE PARENT SLOT (innerTableSlot), not ZoomDock itself
    //     const slotEl = wrapperRef.current?.parentElement as HTMLElement | null;
    //     if (!slotEl) return;
    //
    //     const ro = new ResizeObserver(entries => {
    //         const cr = entries[0]?.contentRect;
    //         if (!cr) return;
    //
    //         const w = Math.max(240, Math.floor(cr.width));
    //         const h = Math.max(180, Math.floor(cr.height));
    //
    //         setSize(prev => (prev.width === w && prev.height === h ? prev : { width: w, height: h }));
    //     });
    //
    //     ro.observe(slotEl);
    //     return () => ro.disconnect();
    // }, [observeParent]);

    // Update Zoom video options when size changes (safe no-op if not supported)
    // useEffect(() => {
    //     try {
    //         clientRef.current?.updateVideoOptions?.({
    //             defaultViewType: viewType,
    //             viewSizes: {
    //                 iewSizes: { default: { width: w, height: h } }
    //             }
    //         });
    //     } catch {
    //         // ignore
    //     }
    // }, [size.width, size.height]);

    const meetingNumber = zoom?.meetingNumber ? String(zoom.meetingNumber) : '';
    const canRun = useMemo(() => {
        return Boolean(show && zoom?.sdkKey && meetingNumber && zoom?.userName && zoom?.signatureEndpoint && zoomRootEl && Window.zoomApi);
    }, [show, zoom?.sdkKey, zoom?.userName, zoom?.signatureEndpoint, meetingNumber, zoomRootEl]);

    const shareUserIdRef = useRef<number | null>(null);

    const syncShareFromRoster = useCallback(() => {
        try {
            const list = Window.zoomApi?.getAttendeeslist?.() ?? [];
            // sharerOn is optional; only treat literal true as active
            const sharer = list.find((p: any) => p?.sharerOn === true);

            if (sharer?.userId) {
                shareUserIdRef.current = sharer.userId;
                setShareActive(true);
            } else {
                shareUserIdRef.current = null;
                setShareActive(false);
            }
        } catch {
            // if roster not ready, don't force true; prefer false until proven otherwise
            shareUserIdRef.current = null;
            setShareActive(false);
        }
    }, []);

    const syncShareWithRetry = useCallback(async () => {
        // roster may populate a moment after join; retry a few times
        for (let i = 0; i < 6; i++) {
            syncShareFromRoster();
            if (shareUserIdRef.current) return;
            await new Promise(r => setTimeout(r, 300));
        }
    }, [syncShareFromRoster]);

    const [zoomScale, setZoomScale] = useState(1);
    const [needsAudioConnect, setNeedsAudioConnect] = useState(false);
    const zoomScaleRef = useRef(1);

    const setZoomScaleBoth = (next: number) => {
        zoomScaleRef.current = next; // immediate, synchronous
        setZoomScale(next); // async render update
    };

    useEffect(() => {
        if (!shareActive) {
            zoomScaleRef.current = 1;
            setZoomScaleBoth(1);
            return;
        }

        // Reset scale baseline when sharing toggles on
        zoomScaleRef.current = 1;
        setZoomScaleBoth(1);

        let raf = 0;
        let resizeDoneTimer: number | null = null;

        const recompute = () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => {
                const stage = wrapperRef.current?.querySelector('.ZoomStage') as HTMLElement | null;
                const content = wrapperRef.current?.querySelector(
                    '[aria-label="Zoom app container"] .react-draggable:not([aria-label="Screen share control bar"])'
                ) as HTMLElement | null;

                if (!stage || !content) return;

                const avail = stage.getBoundingClientRect();
                const rect = content.getBoundingClientRect();

                const availW = avail.width;
                const availH = avail.height;

                // measured sizes (these INCLUDE current scale)
                const measuredW = rect.width;
                const measuredH = rect.height;

                if (!availW || !availH || !measuredW || !measuredH) {
                    zoomScaleRef.current = 1;
                    setZoomScaleBoth(1);
                    return;
                }

                // ✅ CRITICAL: remove current scale from measurement to avoid oscillation
                const currentScale = zoomScaleRef.current || 1;
                const neededW = measuredW / currentScale;
                const neededH = measuredH / currentScale;

                // Fit inside available space
                const s = Math.min(availW / neededW, availH / neededH);

                // Clamp
                const clamped = Math.max(0.6, Math.min(1.6, s));

                // Only update if meaningfully different
                const EPS = 0.01;
                if (Math.abs(clamped - currentScale) > EPS) {
                    setZoomScaleBoth(clamped);
                }

                // Debug (optional)
                // console.log(`scale=${clamped.toFixed(3)} avail=${availW.toFixed(0)}x${availH.toFixed(0)} needed(unscaled)=${neededW.toFixed(0)}x${neededH.toFixed(0)} measured=${measuredW.toFixed(0)}x${measuredH.toFixed(0)} current=${currentScale.toFixed(3)}`);
            });
        };

        // Zoom lays out in phases (toolbar can appear later)
        recompute();
        const t1 = window.setTimeout(recompute, 150);
        const t2 = window.setTimeout(recompute, 400);
        const t3 = window.setTimeout(recompute, 700);

        const onResize = () => {
            // keep it responsive while dragging
            recompute();

            // "resize finished"
            if (resizeDoneTimer) window.clearTimeout(resizeDoneTimer);
            resizeDoneTimer = window.setTimeout(() => {
                recompute();
                resizeDoneTimer = null;
            }, 250);
        };

        window.addEventListener('resize', onResize);

        return () => {
            window.removeEventListener('resize', onResize);
            window.clearTimeout(t1);
            window.clearTimeout(t2);
            window.clearTimeout(t3);
            if (resizeDoneTimer) window.clearTimeout(resizeDoneTimer);
            cancelAnimationFrame(raf);
        };
    }, [shareActive]);

    useEffect(() => {
        if (!joined) return;
        //  setNeedsAudioConnect(true);
        // 1) initialize (covers “already sharing when joining”)
        syncShareWithRetry();

        // 2) keep updated by events
        const handler = (payload: { action: string; userId: number }) => {
            const action = String(payload?.action ?? '').toLowerCase();

            if (action.includes('start')) {
                shareUserIdRef.current = payload.userId ?? null;
                setShareActive(true);
                return;
            }

            if (action.includes('stop')) {
                // hard reset (don’t trust roster timing)
                shareUserIdRef.current = null;
                setShareActive(false);
                // optional: resync shortly after stop in case another user started sharing immediately
                setTimeout(syncShareFromRoster, 250);
                return;
            }

            // unknown action → fall back to roster
            syncShareFromRoster();
        };

        Window.zoomApi?.on?.('peer-share-state-change', handler);

        return () => {
            Window.zoomApi?.off?.('peer-share-state-change', handler);
        };
    }, [joined, syncShareFromRoster, syncShareWithRetry]);

    useEffect(() => {
        const c = Window.zoomApi;
        if (!c) return;

        if (!shareActive) {
            // give Zoom a tick to finish its internal transition back from share
            const t = window.setTimeout(() => {
                // c.updateVideoOptions({
                //     defaultViewType: viewType,
                //     isResizable: true
                //     //popper: { placement: 'top' },
                //     // viewSizes: {
                //     //     default: { width: w, height: h }
                //     // }
                // });
                c.setViewType(viewType); // force the small top-right popper
            }, 500);

            return () => window.clearTimeout(t);
        }
    }, [shareActive]);

    // Join/leave lifecycle:
    // - join when canRun becomes true and we haven't joined this meetingNumber
    // - leave when unmounted OR meeting changes OR show becomes false
    useEffect(() => {
        if (!canRun) {
            // Leave if we were joined or joining
            if (joinedMeetingRef.current || joiningMeetingRef.current) {
                try {
                    Window.zoomApi?.leaveMeeting?.();
                } catch {}
            }
            joinedMeetingRef.current = null;
            joiningMeetingRef.current = null;
            joinInFlightRef.current = false;
            initDoneRef.current = false;
            setJoined(false);
            setJoining(false);
            return;
        }

        // Already joined OR already joining this meeting => do nothing
        if (joinedMeetingRef.current === meetingNumber) return;
        if (joiningMeetingRef.current === meetingNumber) return;
        if (joinInFlightRef.current) return;

        let cancelled = false;

        const run = async () => {
            joinInFlightRef.current = true;
            joiningMeetingRef.current = meetingNumber;

            setJoining(true);
            setJoined(false);

            try {
                // init only once per mount/session (re-init while joining can also cause trouble)
                if (!initDoneRef.current) {
                    await Window.zoomApi.init({
                        zoomAppRoot: zoomRootEl,
                        language: zoom!.language ?? 'en-US',
                        patchJsMedia: true,

                        videoOptions: {
                            defaultViewType: viewType,
                            isResizable: true
                            //popper: { placement: 'top' },
                            // viewSizes: {
                            //     default: { width: w, height: h }
                            // }
                        },
                        isAllowToTalk: false,
                        customize: {
                            patchJsMedia: true,
                            video: {
                                // popper: {
                                //     anchorPosition: { top: 0, right: 0 },
                                //     disableDraggable: false,
                                // },
                                isResizable: true,
                                // viewSizes: {
                                //     default: { width: w, height: h }
                                // },
                                defaultViewType: viewType
                            },
                            chat: {
                                notificationCls: { top: 12, left: 12 },
                                popper: {
                                    anchorReference: 'anchorPosition',
                                    anchorPosition: { top: 12, right: 12 },
                                    placement: 'bottom-start',
                                    // Optional: keep it from flipping to other corners
                                    modifiers: [
                                        // { name: 'flip', enabled: false },
                                        { name: 'preventOverflow', options: { boundary: 'viewport' } }
                                    ]
                                }
                            },
                            participants: {
                                popper: {
                                    anchorReference: 'anchorPosition',
                                    anchorPosition: { top: 12, right: 12 },
                                    placement: 'bottom-start',
                                    // Optional: keep it from flipping to other corners
                                    modifiers: [
                                        // { name: 'flip', enabled: false },
                                        { name: 'preventOverflow', options: { boundary: 'viewport' } }
                                    ]
                                }
                            },
                            activeApps: {
                                popper: {
                                    anchorReference: 'anchorPosition',
                                    anchorPosition: { top: 12, right: 12 },
                                    placement: 'bottom-start',
                                    // Optional: keep it from flipping to other corners
                                    modifiers: [
                                        // { name: 'flip', enabled: false },
                                        { name: 'preventOverflow', options: { boundary: 'viewport' } }
                                    ]
                                }
                            },
                            setting: {
                                popper: {
                                    anchorReference: 'anchorPosition',
                                    anchorPosition: { top: 12, right: 12 },
                                    placement: 'bottom-start',
                                    // Optional: keep it from flipping to other corners
                                    modifiers: [
                                        // { name: 'flip', enabled: false },
                                        { name: 'preventOverflow', options: { boundary: 'viewport' } }
                                    ]
                                }
                            },
                            meetingInfo :['participant']
                        },
                        leaveOnPageUnload: true,
                        maximumVideosInGalleryView: 2
                    });
                    initDoneRef.current = true;
                }

                if (cancelled) return;

                await Window.zoomApi.join({
                    signature: zoom!.signatureEndpoint,
                    meetingNumber: zoom!.meetingNumber,
                    password: zoom!.password ?? '',
                    userName: zoom!.userName,
                    userEmail: zoom!.userEmail ?? zoom!.userName,
                });

                if (cancelled) return;

                joinedMeetingRef.current = meetingNumber;
                setNeedsAudioConnect(true);
                setJoined(true);
                onConnect?.({
                    connectAudio: clickZoomJoinAudio
                });
            } catch (e) {
                console.error('Zoom Component View error:', e);
                joinedMeetingRef.current = null;
                initDoneRef.current = false;
                if (!cancelled) setJoined(false);
            } finally {
                joinInFlightRef.current = false;
                joiningMeetingRef.current = null;
                if (!cancelled) setJoining(false);
            }
        };

        run();

        return () => {
            cancelled = true;
        };
        // IMPORTANT: do NOT depend on size here, or it can restart joins during resizes.
    }, [canRun, meetingNumber, zoom?.sdkKey, zoom?.signatureEndpoint, zoom?.userName, zoomRootEl]);

    // Leave on unmounting (true cleanup)
    useEffect(() => {
        return () => {
            try {
                Window.zoomApi?.leaveMeeting?.();
            } catch {}
            joinedMeetingRef.current = null;
        };
    }, []);

    const clickZoomJoinAudio = async () => {
        try {
            // 1. unlock playback on user-gesture
            //await unlockBrowserAudio();

            try {
                const url = getSoundFileUrl('zoomStart');
                if (url) {
                    const audio = new Audio(url);
                    await audio.play();
                }
            } catch (error) {
                console.warn('Audio unlock failed', error);
            }

            // 2. optionally preflight mic permission
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: true,
                video: false,
            });
            stream.getTracks().forEach(track => track.stop());
        } catch (error) {
            console.error('Error requesting mic permission', error);
        }

        const root = wrapperRef.current?.querySelector('[aria-label="Zoom app container"]') as HTMLElement | null;
        if (!root) return;
        const buttons = Array.from(root.querySelectorAll('button'));
        console.log(
            buttons.map((btn) => ({
                ariaLabel: btn.getAttribute('aria-label'),
                title: btn.getAttribute('title'),
                text: btn.textContent?.trim(),
                dataTestId: btn.getAttribute('data-testid')
            })));
        const btn =
            root.querySelector('button[aria-label*="Join Audio"]') ||
            root.querySelector('button[aria-label*="Connect Audio"]') ||
            root.querySelector('button[aria-label*="Audio"]') ||
            root.querySelector('button[title*="Audio"]');

        (btn as HTMLButtonElement | null)?.click();
    };

    return (
        <div
            ref={wrapperRef}
            className={['ZoomDock', shareActive ? 'isShareActive' : 'isShareNotActive', className].filter(Boolean).join(' ')}
            style={shareActive ? ({ ['--zoomScale' as any]: zoomScale } as React.CSSProperties) : undefined}
        >
            <div className="ZoomStage">
                <div ref={zoomRootRef} className="ZoomDockRoot" />
            </div>
            {!joined && (
                <div
                    className="ZoomDockOverlay"
                    style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'grid',
                        placeItems: 'center',
                        background: 'rgba(0,0,0,0.35)',
                        color: '#fff',
                        pointerEvents: 'none'
                    }}
                >
                    {joining ? 'Connecting…' : `${zoom!.meetingNumber}`}
                </div>
            )}
        </div>
    );
}
